import Repository from '../../repositories/RepositoryFactory';
const ccmRecordsRepository = Repository.get('ccmRecords');

export default {
	namespaced: true,
	state: {
		ccmRecordsByPatientId: [],
		ccmRecordsByHospitalId: [],
		ccmRecord: null,
		ccmRecordLoadingStatus: false,
		ccmRecordTitleTypeOptions: [],
	},
	mutations: {
		SET_CMM_RECORDS_BY_PATIENT_ID (state, payload) {
			state.ccmRecordsByPatientId = payload;
		},

		SET_CMM_RECORDS_BY_HOSPITAL_ID (state, payload) {
			state.ccmRecordsByHospitalId = payload;
		},

		SET_CCM_RECORD (state, payload) {
			state.ccmRecord = payload;
		},

		UPDATE_CCM_RECORD (state, payload) {
			state.ccmRecord = {
				...state.ccmRecord,
				...payload,
			};
		},

		UPDATE_CCM_RECORD_IN_PATIENT_LIST (state, payload) {
			const index = state.ccmRecordsByPatientId.findIndex(
				(record) => record.id === payload.id,
			);
			if (index !== -1) {
				state.ccmRecordsByPatientId.splice(index, 1, {
					...state.ccmRecordsByPatientId[index],
					...payload,
				});
			}
		},

		ADD_NEW_CCM_RECORD_TO_PATIENT_LIST (state, payload) {
			state.ccmRecordsByPatientId.unshift(payload);
		},

		TOGGLE_CCM_RECORD_LOADING_STATUS (state, payload) {
			state.ccmRecordLoadingStatus = payload;
		},

		REMOVE_CCM_RECORD (state, id) {
			state.ccmRecordsByPatientId = state.ccmRecordsByPatientId.filter(
				(record) => record.id !== id,
			);
			if (!state.ccmRecordsByPatientId.length || !state.ccmRecord) {
				state.ccmRecord = null;
			} else if (state.ccmRecord.id === id) {
				state.ccmRecord = null;
			}
		},
	},

	actions: {
		async storeMinimalCCMRecordsByPatientId ({ commit }, patientId) {
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', true);
			const response =
				await ccmRecordsRepository.getMinimalCCMRecordsByPatientId(patientId);
			commit('SET_CMM_RECORDS_BY_PATIENT_ID', response.data);
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', false);

			return response.data[0];
		},
		async storeCCMRecordsByPatientId ({ commit }, patientId) {
			const response = await ccmRecordsRepository.getCCMRecordsByPatientId(
				patientId,
			);
			const sortedResponse = [...response].sort((a, b) => {
				const dateA = new Date(a.date);
				const dateB = new Date(b.date);
				return dateB - dateA;
			});

			commit('SET_CMM_RECORDS_BY_PATIENT_ID', sortedResponse);

			return sortedResponse.data[0];
		},
		async storeCCMRecordsByHospitalId ({ commit }, hospitalId) {
			const response = await ccmRecordsRepository.getCCMRecordsByHospitalId(
				hospitalId,
			);
			commit('SET_CMM_RECORDS_BY_HOSPITAL_ID', response.data);
		},
		async getSelectedCCMRecordId ({ commit }, id) {
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', true);
			const response = await ccmRecordsRepository.getCCMRecord(id);
			commit('SET_CCM_RECORD', response.data);
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', false);
		},
		async updateCCMRecord ({ commit }, request) {
			return await ccmRecordsRepository.updateCCMRecord(
				request.id,
				request.body,
			);
		},
		async deleteCCMRecord ({ commit }, id) {
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', true);
			commit('REMOVE_CCM_RECORD', id);
			const response = await ccmRecordsRepository.deleteCCMRecord(id);
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', false);
			return response;
		},
		async getAllConditionsTemplates () {
			return await ccmRecordsRepository.getAllConditionsTemplates();
		},
		async getAllCarePlanTypes () {
			return await ccmRecordsRepository.getAllCarePlanTypes();
		},
		async getAllCarePlanStatuses () {
			return await ccmRecordsRepository.getAllCarePlanStatuses();
		},
		async getSocialDeterminantsTemplate () {
			return await ccmRecordsRepository.getSocialDeterminantsTemplate();
		},
		async getComplexDecisionMakingTemplate () {
			return await ccmRecordsRepository.getComplexDecisionMakingTemplate();
		},
		async getFunctionalStatusTemplate () {
			return await ccmRecordsRepository.getFunctionalStatusTemplate();
		},
		async getAllDiagnosisTypes () {
			return await ccmRecordsRepository.getAllDiagnosisTypes();
		},
		async getMedicationFrequencies () {
			return await ccmRecordsRepository.getMedicationFrequencies();
		},
		async getMedicationEventTypeOption () {
			return await ccmRecordsRepository.getMedicationEventTypeOption();
		},
		async getConditionSeverityOption () {
			return await ccmRecordsRepository.getConditionSeverityOption();
		},
		async getConditionReactionOption () {
			return await ccmRecordsRepository.getConditionReactionOption();
		},
		async getConditionAllergenOption () {
			return await ccmRecordsRepository.getConditionAllergenOption();
		},
		async getConditionEventTypeOption () {
			return await ccmRecordsRepository.getConditionEventTypeOption();
		},
		async getMedicalNoteTitleOption () {
			return await ccmRecordsRepository.getMedicalNoteTitleOption();
		},
		async getMedicalNoteTypeOption () {
			return await ccmRecordsRepository.getMedicalNoteTypeOption();
		},
		async getVitalTypeOptionTemplate () {
			return await ccmRecordsRepository.getVitalTypeOptionTemplate();
		},
		async getVitalUnitOptionTemplate () {
			return await ccmRecordsRepository.getVitalUnitOptionTemplate();
		},
		async getMedicalRoleOptionTemplate () {
			return await ccmRecordsRepository.getMedicalRoleOptionTemplate();
		},
		async getObservationCategoryOptionsTemplate () {
			return await ccmRecordsRepository.getObservationCategoryOptionsTemplate();
		},
		async getVaccineNameTemplate () {
			return await ccmRecordsRepository.getVaccineNameTemplate();
		},
		async getVaccineSiteTemplate () {
			return await ccmRecordsRepository.getVaccineSiteTemplate();
		},
		async getVaccineRouteTemplate () {
			return await ccmRecordsRepository.getVaccineRouteTemplate();
		},
		async getVaccineDoseTemplate () {
			return await ccmRecordsRepository.getVaccineDoseTemplate();
		},
		async getVaccineManufacturerTemplate () {
			return await ccmRecordsRepository.getVaccineManufacturerTemplate();
		},
		async getImagingModalityTemplate () {
			return await ccmRecordsRepository.getImagingModalityTemplate();
		},
		async getImagingBodyPartTemplate () {
			return await ccmRecordsRepository.getImagingBodyPartTemplate();
		},
		async getLabPanelGroupTemplate () {
			return await ccmRecordsRepository.getLabPanelGroupTemplate();
		},
		async getLabSpecimenTypeTemplate () {
			return await ccmRecordsRepository.getLabSpecimenTypeTemplate();
		},
		async getLabInterpretationTemplate () {
			return await ccmRecordsRepository.getLabInterpretationTemplate();
		},
		async getLabUnitTemplate () {
			return await ccmRecordsRepository.getLabUnitTemplate();
		},
		async getLabCriticalFlagTemplate () {
			return await ccmRecordsRepository.getLabCriticalFlagTemplate();
		},
		async getLabSourceTemplate () {
			return await ccmRecordsRepository.getLabSourceTemplate();
		},
		async getDocumentTypeTemplate () {
			return await ccmRecordsRepository.getDocumentTypeTemplate();
		},
		async getConsentTypeMethodTemplate () {
			return await ccmRecordsRepository.getConsentTypeMethodTemplate();
		},
		async getConsentTypeTemplate () {
			return await ccmRecordsRepository.getConsentTypeTemplate();
		},
		async getCCMReportDataForMonthByPatientId ({ commit }, patientId) {
			return await ccmRecordsRepository.getCCMReportDataForMonthByPatientId(
				patientId,
			);
		},
		async addCCMRecord ({ commit }, body) {
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', true);
			const response = await ccmRecordsRepository.addCCMRecord(body);
			commit('ADD_NEW_CCM_RECORD_TO_PATIENT_LIST', response.data);
			commit('TOGGLE_CCM_RECORD_LOADING_STATUS', false);
			return response;
		},
		async updateSocialDeterminants ({ commit }, request) {
			return await ccmRecordsRepository.updateSocialDeterminants(
				request.id,
				request.body,
			);
		},
		async updateComplexDecisionMaking ({ commit }, request) {
			return await ccmRecordsRepository.updateComplexDecisionMaking(
				request.id,
				request.body,
			);
		},
		async updateFunctionalStatus ({ commit }, request) {
			return await ccmRecordsRepository.updateFunctionalStatus(
				request.id,
				request.body,
			);
		},
	},
};
